import { useState, useEffect } from "react";
import Slider from "react-slick";
import "../../styles/Slider.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import apiClient from "../../Api/apiConfig";
import FileUploader from "../grievance/FileUpload";

const SliderManager = () => {
  const [images, setImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [newAltText, setNewAltText] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const API_URL = "/slider";

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await apiClient.get(API_URL);
        setImages(response.data);
      } catch (err) {
        console.error("Erreur lors de la récupération des images :", err.message);
        setError("Impossible de charger les images.");
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const handleAddImage = async () => {
    if (uploadedFiles.length === 0 || !newAltText.trim()) {
      alert("Veuillez sélectionner une image et fournir un texte alternatif.");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadedFiles[0]);
    formData.append("alt_text", newAltText.trim());
    formData.append("description", newDescription.trim());

    try {
      const response = await apiClient.post(API_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages([...images, response.data]);
      alert("Image ajoutée avec succès !");
      resetForm();
    } catch (err) {
      console.error("Erreur lors de l’ajout de l’image :", err.message);
      alert("Erreur lors de l’ajout de l’image.");
    }
  };

  const handleEditImage = async () => {
    if (!currentImage || !newAltText.trim()) {
      alert("Veuillez fournir un texte alternatif.");
      return;
    }

    const formData = new FormData();
    formData.append("alt_text", newAltText.trim());
    formData.append("description", newDescription.trim());
    if (uploadedFiles.length > 0) {
      formData.append("file", uploadedFiles[0]);
    }

    try {
      const response = await apiClient.put(`${API_URL}/${currentImage.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setImages((prev) =>
        prev.map((img) =>
          img.id === currentImage.id ? { ...img, ...response.data } : img
        )
      );
      alert("Image modifiée avec succès !");
      resetForm();
    } catch (err) {
      console.error("Erreur lors de la modification de l’image :", err.message);
      alert("Erreur lors de la modification de l’image.");
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette image ?")) return;

    try {
      await apiClient.delete(`${API_URL}/${id}`);
      setImages((prev) => prev.filter((img) => img.id !== id));
      alert("Image supprimée avec succès !");
    } catch (err) {
      console.error("Erreur lors de la suppression de l’image :", err.message);
      alert("Erreur lors de la suppression de l’image.");
    }
  };

  const resetForm = () => {
    setUploadedFiles([]);
    setPreview(null);
    setNewAltText("");
    setNewDescription("");
    setCurrentImage(null);
    setIsEditing(false);
    setShowPopup(false);
  };

  const handleFileChange = (files) => {
    setUploadedFiles(files);
    if (files.length > 0) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(files[0]);
    }
  };

  const openEditPopup = (image) => {
    setCurrentImage(image);
    setNewAltText(image.alt_text);
    setNewDescription(image.description);
    setIsEditing(true);
    setShowPopup(true);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 768,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  if (loading) return <p>Chargement en cours...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="slider-manager">
      <h2>Gestion des Images du Slider</h2>
      <button onClick={() => setShowPopup(true)} className="add-image-button">
        Ajouter une Image
      </button>

      <Slider {...sliderSettings}>
        {images.map((image) => (
          <div key={image.id}>
            <img
              src={`${apiClient.defaults.baseURL}/uploads/${image.url}`}
              alt={image.alt_text || "Image indisponible"}
              className="slider-image"
            />
          </div>
        ))}
      </Slider>

      <table className="slider-images-table">
        <thead>
          <tr>
            <th>Miniature</th>
            <th>Texte alternatif</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {images.map((image) => (
            <tr key={image.id}>
              <td>
                <img
                  src={`${apiClient.defaults.baseURL}/uploads/${image.url}`}
                  alt={image.alt_text || "Image indisponible"}
                  className="slider-image-thumbnail"
                />
              </td>
              <td>{image.alt_text || "Non spécifié"}</td>
              <td>{image.description || "Aucune description"}</td>
              <td className="action-buttons">
                <button onClick={() => openEditPopup(image)}>Modifier</button>
                <button onClick={() => handleDeleteImage(image.id)}>Supprimer</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showPopup && (
        <div className="popup">
          <div className="popup-content">
            <h3>{isEditing ? "Modifier une Image" : "Ajouter une Image"}</h3>
            <FileUploader onFilesChange={handleFileChange} />
            {preview && (
              <div className="image-preview">
                <img src={preview} alt="Aperçu" />
              </div>
            )}
            <input
              type="text"
              placeholder="Texte alternatif"
              value={newAltText}
              onChange={(e) => setNewAltText(e.target.value)}
            />
            <textarea
              placeholder="Description"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            <button onClick={isEditing ? handleEditImage : handleAddImage}>
              {isEditing ? "Modifier" : "Ajouter"}
            </button>
            <button onClick={resetForm}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SliderManager;
